/**
 * Load now, and again every `ms` while the app is actually in front of someone.
 *
 * Every panel needs this and every panel had its own copy of it in the Angular client. Polling
 * while backgrounded is what turns a couple's app into a battery complaint, and on a phone it is
 * AppState rather than document.visibilityState that knows.
 *
 * IT ALSO OWNS "HAS THIS EVER LOADED", because nothing else was. Every screen swallowed its own
 * failures into a bare `catch {}` and rendered its reassuring empty state — "No open missions",
 * "The card bank is empty" — before the first fetch had even resolved, and again when a 500 came
 * back. A person could not tell an empty list from a broken one, and a slow connection said the
 * game had nothing for them today. The two facts a panel needs to answer that are here, once:
 *
 *   `loaded`  the first attempt has settled, whichever way it settled. Skeletons until then.
 *   `error`   the last failure, while nothing has EVER loaded. An error card, and a way back.
 *
 * Once a load has succeeded those two never change again: a later poll that fails leaves the
 * content that is already on screen exactly where it is, because stale is the correct answer to a
 * dropped connection and the shell's Reconnecting banner is already saying so.
 *
 * A loader passed in here must therefore let its fetch THROW. Wrapping the whole thing in a
 * try/catch again is how a panel goes back to reporting "nothing to do" for "I could not ask".
 */
import * as React from 'react';
import { AppState } from 'react-native';

export interface PollState {
  /** True once the first attempt has settled, however it settled. */
  loaded: boolean;
  /** The last failure, while nothing has ever loaded; cleared by any success. */
  error: string | null;
  /** Run the loader again now — what a Try again button presses. */
  reload: () => void;
}

/** What a thrown thing is called, without ever rendering "[object Object]" at somebody. */
function messageOf(e: unknown): string {
  const message = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return message.trim() || 'The server did not answer.';
}

export function usePoll(load: () => void | Promise<unknown>, ms: number): PollState {
  const latest = React.useRef(load);
  latest.current = load;

  const [state, setState] = React.useState<{ loaded: boolean; error: string | null }>({
    loaded: false,
    error: null,
  });

  /** A poll in flight outlives one tab press, and setting state on the way out is a warning at
   *  best and a leak at worst. */
  const alive = React.useRef(true);
  const succeeded = React.useRef(false);

  const run = React.useCallback(async () => {
    try {
      await latest.current();
      succeeded.current = true;
      if (alive.current) setState({ loaded: true, error: null });
    } catch (e) {
      const message = messageOf(e);
      // A failure after a success is not news: the content on screen stays, and the shell's
      // banner is what says the connection went.
      if (alive.current && !succeeded.current) setState({ loaded: true, error: message });
    }
  }, []);

  const reload = React.useCallback(() => {
    setState((s) => (s.error ? { loaded: false, error: null } : s));
    void run();
  }, [run]);

  React.useEffect(() => {
    alive.current = true;
    const tick = () => {
      if (AppState.currentState === 'active') void run();
    };
    void run();
    const timer = setInterval(tick, ms);
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') tick();
    });
    return () => {
      alive.current = false;
      clearInterval(timer);
      sub.remove();
    };
  }, [ms, run]);

  return { loaded: state.loaded, error: state.error, reload };
}
